import React, { useCallback, useEffect, useState } from 'react';

import { EmptyState, ErrorState, ListaSelecao, Modal, Pagination, Skeleton } from '../../../ui';

import {
  fetchSgaTipoVeiculos,
  TipoVeiculo,
} from '../../../api/sga-tipo-veiculo/sga-tipo-veiculoApi';
import { extrairMensagemErro } from '../../../utils/apiError';

/**
 * Escolha do tipo de veículo (reescrito na at-59 sobre o `Modal` do kit).
 *
 * ## O que a reescrita mudou
 *
 *   1. **Visual do design system**, lista alcançável pelo teclado (era
 *      `<li onClick>`) e paginação em `<button>` (era `<div onClick>`).
 *   2. **Falha deixou de ser muda** — era toast + lista vazia, indistinguível de
 *      "nenhum tipo encontrado".
 *   3. O `Math.ceil` da paginação saiu para dentro do `Pagination`.
 *
 * ⚠️ **As props não mudaram** (`isOpen`/`onClose`/`onSelectTipoVeiculo`/
 * `somenteComPlano`). 📌 **Atualização da at-62:** `views/cotacao` foi migrada —
 * e continua sendo ela a única que passa `somenteComPlano`, porque é lá que o
 * tipo vira o `codigoPlano` do PDF.
 * ⚠️ **A paginação daqui já estava correta** (mandava `page`/`limit`), ao
 * contrário das de cores e combustíveis.
 * 📌 O vazio com `somenteComPlano` continua explicando o caminho: só acontece se
 * nenhum plano estiver ativo, e o TI resolve em Administração › Planos de Cotação,
 * sem depender de deploy (at-43).
 */

interface ModalTipoVeiculosProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectTipoVeiculo: (tipoVeiculo: TipoVeiculo) => void;
  /**
   * Recorte da tela de COTAÇÃO: só os tipos que têm plano de cotação ativo
   * (at-43). Sem a flag vale o recorte histórico das telas de veículo.
   */
  somenteComPlano?: boolean;
}

type Estado = 'carregando' | 'ok' | 'erro';

const POR_PAGINA = 10;

const ModalTipoVeiculos: React.FC<ModalTipoVeiculosProps> = ({
  isOpen,
  onClose,
  onSelectTipoVeiculo,
  somenteComPlano,
}) => {
  const [tipos, setTipos] = useState<TipoVeiculo[]>([]);
  const [total, setTotal] = useState(0);
  const [estado, setEstado] = useState<Estado>('carregando');
  const [erro, setErro] = useState('');
  const [pagina, setPagina] = useState(1);

  useEffect(() => {
    if (isOpen) setPagina(1);
  }, [isOpen]);

  const buscar = useCallback(async () => {
    setEstado('carregando');
    try {
      const resposta = await fetchSgaTipoVeiculos(pagina, POR_PAGINA, somenteComPlano);

      if (!resposta || !Array.isArray(resposta.items)) {
        setErro('A resposta do servidor não trouxe a lista de tipos de veículo.');
        setEstado('erro');
        return;
      }

      setTipos(resposta.items);
      setTotal(resposta.total ?? 0);
      setEstado('ok');
    } catch (falha) {
      setErro(extrairMensagemErro(falha, 'Não foi possível carregar os tipos de veículo.'));
      setEstado('erro');
    }
  }, [pagina, somenteComPlano]);

  useEffect(() => {
    if (isOpen) void buscar();
  }, [isOpen, buscar]);

  return (
    <Modal
      aberto={isOpen}
      titulo="Selecionar Tipo de Veículo"
      descricao="Define a cota, os produtos e o rateio do veículo."
      tamanho="sm"
      aoFechar={onClose}
      rodape={
        estado === 'ok' && tipos.length > 0 ? (
          <Pagination
            pagina={pagina}
            totalItens={total}
            porPagina={POR_PAGINA}
            aoTrocar={setPagina}
          />
        ) : undefined
      }
    >
      {estado === 'carregando' && <Skeleton linhas={4} altura="tw-h-11" />}

      {estado === 'erro' && <ErrorState mensagem={erro} aoTentarNovamente={() => void buscar()} />}

      {estado === 'ok' && (
        <ListaSelecao
          itens={tipos}
          chave={(t) => t.codigo_tipo}
          rotulo={(t) => t.descricao_tipo}
          aoEscolher={(t) => {
            onSelectTipoVeiculo(t);
            onClose();
          }}
          rotuloAcessivel="Tipos de veículo"
          vazio={
            somenteComPlano ? (
              <EmptyState
                icone="sem-dados"
                titulo="Nenhum tipo de veículo disponível para cotação"
                descricao="Verifique os Planos de Cotação na tela de Administração (TI) — um tipo só aparece aqui depois de ter plano ativo."
              />
            ) : (
              <EmptyState
                icone="sem-dados"
                titulo="Nenhum tipo de veículo encontrado"
                descricao="O espelho de tipos do SGA parece vazio. Fale com o TI."
              />
            )
          }
        />
      )}
    </Modal>
  );
};

export default ModalTipoVeiculos;
